// Display-only gallery. The picker owns variant IDs, availability and order properties.
export const normalise = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const hasPhrase = (text, phrase) => !!phrase && (' ' + text + ' ').includes(' ' + normalise(phrase) + ' ');
const sameFinish = (a, b) => !!a && !!b && a.fabric === b.fabric && a.key === b.key;
const finishLabel = finish => finish ? [finish.fabricLabel, finish.colour].filter(Boolean).join(' · ') : '';
const configKey = config => JSON.stringify((config || []).map(x => [normalise(x.name), normalise(x.value)]));
const unique = values => [...new Set(values)];

export function buildGalleryPlan(data, state) {
  const records = state.records || [];
  const media = data.media || [];
  const curation = data.curation || {};
  const lifestyleIds = (curation.lifestyleIds || []).map(String);
  const selected = state.fabric && state.colour ? state : null;
  const textOf = item => normalise(item.alt + ' ' + (item.src || '').split('/').pop().split('?')[0]);
  const linked = item => records.filter(record => String(record.mediaId) === String(item.id));
  const finishOf = item => {
    const attached = linked(item);
    const finishes = unique(attached.map(record => record.fabric + '|' + record.key));
    // Current native image associations take precedence over old filenames/alt labels.
    if (finishes.length === 1) return attached[0];
    if (finishes.length > 1) return null;
    const text = textOf(item);
    const candidates = (state.finishes || []).flatMap(finish => {
      const matched = (finish.aliases || [finish.colour]).filter(alias => hasPhrase(text, alias));
      return matched.length ? [{...finish, score: Math.max(...matched.map(alias => normalise(alias).length))}] : [];
    });
    const explicitFabric = unique((state.finishes || []).filter(finish => hasPhrase(text, finish.fabric)).map(finish => finish.fabric));
    const filtered = explicitFabric.length === 1 ? candidates.filter(finish => finish.fabric === explicitFabric[0]) : candidates;
    if (!filtered.length) return null;
    const longest = Math.max(...filtered.map(finish => finish.score));
    const best = filtered.filter(finish => finish.score === longest);
    const identities = unique(best.map(finish => finish.fabric + '|' + finish.key));
    if (identities.length === 1) return best[0];
    // A colour shared by fabrics is only inferred when this product has one established mapping.
    const established = best.filter(finish => records.some(record => sameFinish(record, finish)));
    return unique(established.map(finish => finish.fabric + '|' + finish.key)).length === 1 ? established[0] : null;
  };
  const kindOf = item => {
    if (item.type !== 'image') return item.type;
    const text = textOf(item);
    if (/\b(dimensions?|measurements?|size guide|drawing|sketch)\b/.test(text)) return 'dimensions';
    const lifestyleIndex = lifestyleIds.indexOf(String(item.id));
    if (lifestyleIndex !== -1) return lifestyleIndex === 0 ? 'lifestyle' : 'angle';
    if (linked(item).length) return 'clean';
    if (/\b(overhead|rear view|side view|view from|elevated|angle|close up|detail)\b/.test(text) || /lifestyle (left|right)/.test(text)) return 'angle';
    if (/\b(lifestyle|living room|room hero|designer home|premium room)\b/.test(text)) return 'lifestyle';
    if (/\b(clean|plain|isolated|catalogue|studio|cutout)\b/.test(text)) return 'clean';
    if (String(item.id) === String(data.featuredId)) return 'clean';
    // Unlabelled alternate colour shots must not become a second product-photo stack.
    return 'unknown';
  };
  const configCompatible = item => {
    const attached = linked(item);
    if (attached.length) return attached.some(record => record.matchesConfiguration);
    const text = textOf(item);
    const productSeats = normalise(data.title).match(/\b([234]) seater\b/);
    const imageSeats = text.match(/\b([234]) seater\b/);
    if (productSeats && imageSeats && productSeats[1] !== imageSeats[1]) return false;
    return (state.configuration || []).every(option => {
      const values = unique(records.flatMap(record => (record.configuration || []).filter(x => normalise(x.name) === normalise(option.name)).map(x => x.value)));
      const marked = values.filter(value => hasPhrase(text, value) || (/^(box|pillow)\b/i.test(value) && hasPhrase(text, value.split(/\s/)[0])) || (/\b(LHF|RHF)\b/.test(value) && hasPhrase(text, value.match(/\b(LHF|RHF)\b/)[0])));
      return !marked.length || marked.some(value => normalise(value) === normalise(option.value));
    });
  };
  const candidates = media.filter(item => item.type === 'image' && kindOf(item) === 'clean');
  const exact = selected && candidates.find(item => sameFinish(finishOf(item), selected) && configCompatible(item));
  const reference = candidates.find(configCompatible) || candidates[0] || media.find(item => item.type === 'image') || media[0];
  const main = exact || reference;
  if (!main) return {items: [], selected, exact: false};
  const referenceFinish = finishOf(reference);
  const shared = media.filter(item => item.id !== main.id && configCompatible(item));
  const lifestyles = shared.filter(item => kindOf(item) === 'lifestyle');
  const hero = lifestyles.find(item => /\bhero\b/.test(textOf(item))) || lifestyles[0];
  const curatedLifestyle = lifestyleIds.map(id => shared.find(item => String(item.id) === id)).filter(Boolean);
  const angles = shared.filter(item => kindOf(item) === 'angle' && !lifestyleIds.includes(String(item.id)) && (!finishOf(item) || !referenceFinish || sameFinish(finishOf(item), referenceFinish)) && (!curatedLifestyle.length || !/\b(lifestyle|living room|room hero|designer home)\b/.test(textOf(item))));
  const dimensions = shared.filter(item => kindOf(item) === 'dimensions');
  const videos = shared.filter(item => item.type === 'video' || item.type === 'external_video');
  const preferredVideo = videos.find(item => String(item.id) === String(curation.videoId)) || videos.find(item => /\b(studio|scale|seated model|adult model|comfort film)\b/.test(textOf(item))) || videos[0];
  const models = shared.filter(item => item.type === 'model');
  const ordered = [main, preferredVideo, ...(curatedLifestyle.length ? curatedLifestyle : [hero]), ...angles, ...dimensions, ...videos.filter(item => item !== preferredVideo), ...models].filter(Boolean);
  const seen = new Set();
  const items = ordered.filter(item => !seen.has(String(item.id)) && seen.add(String(item.id))).map((item, index) => {
    const finish = finishOf(item);
    const attached = linked(item);
    const isMain = index === 0;
    const kind = kindOf(item);
    const isVideo = item.type === 'video' || item.type === 'external_video';
    const medium = isVideo ? 'Video' : 'Photo';
    let caption = '';
    if (kind === 'dimensions') caption = 'Dimensions guide for this model. Check your selected size and configuration below.';
    else if (finish) {
      caption = medium + ' shown in ' + finishLabel(finish) + '.';
      if (isMain && selected && !exact) caption += ' Your selected finish or configuration isn’t pictured; this is a reference photo.';
      else if (selected && !sameFinish(finish, selected)) caption += ' Your selected finish is shown above.';
    } else {
      caption = medium + ' shown for reference; the finish pictured is not confirmed.';
      if (isMain && selected) caption += ' Your selected finish isn’t pictured.';
    }
    const configs = unique(attached.map(record => configKey(record.configuration)));
    if (configs.length > 1 || (attached.length && !configCompatible(item))) caption += ' Layout shown is a reference; follow your selected configuration below.';
    else if (!attached.length && kind !== 'dimensions') {
      // Preserve verified descriptive alt text, including cushion style and range-demo context.
      const description = String(item.alt || '').trim();
      if (description) caption += ' ' + description;
      if ((state.configuration || []).length) caption += ' Check your selected configuration below.';
    }
    return {...item, kind, finish, caption, isMain, exact: isMain && !!exact,
      label: isMain ? (exact ? 'Selected finish' : 'Reference photo') : isVideo ? 'Video' : kind === 'lifestyle' ? 'Lifestyle' : kind === 'dimensions' ? 'Dimensions' : kind === 'model' ? '3D view' : 'Detail view'};
  });
  return {items, selected, exact: !!exact};
}

if (typeof customElements !== 'undefined' && !customElements.get('ck-finish-gallery')) {
  customElements.define('ck-finish-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.data = JSON.parse(this.querySelector('[data-gallery-data]').textContent);
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.dialog = this.querySelector('dialog');
      this.scope = this.closest('.shopify-section') || document;
      this.scope.addEventListener('ck:finish-selected', event => this.update(event.detail), {signal});
      this.scope.addEventListener('variant:update', () => queueMicrotask(() => this.readPicker()), {signal});
      document.addEventListener('DOMContentLoaded', () => this.readPicker(), {signal, once:true});
      this.addEventListener('click', event => this.click(event), {signal});
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.markActive(Math.round(this.track.scrollLeft / (this.track.clientWidth || 1))));
      }, {signal, passive:true});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        this.select(event.key === 'Home' ? 0 : event.key === 'End' ? this.plan.items.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, {signal});
      this.addEventListener('play', event => {
        if (event.target.tagName === 'VIDEO') document.querySelectorAll('video').forEach(video => {if(video !== event.target) video.pause();});
      }, {signal, capture:true});
      this.resizeObserver = new ResizeObserver(() => {if(this.plan) this.select(this.index, false);});
      this.resizeObserver.observe(this.track);
      this.readPicker();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resizeObserver?.disconnect(); cancelAnimationFrame(this.scrollFrame);
      this.querySelectorAll('video').forEach(video => video.pause());
    }
    readPicker() {
      const picker = this.scope.querySelector('[data-ck-linked-picker][data-ck-standard-fabric="true"]');
      if (picker?.__ckGallerySelection) this.update(picker.__ckGallerySelection);
      else if (!picker) this.update({handle:this.data.handle, configuration:[], records:[], finishes:[]});
    }
    update(state) {
      if (state?.handle !== this.data.handle) return;
      const key = JSON.stringify([state.fabric,state.key,state.configuration]);
      if (key === this.selectionKey) return;
      this.selectionKey = key;
      this.plan = buildGalleryPlan(this.data, state);
      if (!this.plan.items.length) return;
      this.querySelectorAll('video').forEach(video => video.pause());
      if(this.dialog.open) this.dialog.close();
      this.track.replaceChildren(); this.thumbs.replaceChildren();
      this.plan.items.forEach((item,index) => {
        const slide = document.createElement('div');
        slide.className = 'ck-finish-gallery__slide'; slide.dataset.mediaId = item.id;
        slide.setAttribute('role','group'); slide.setAttribute('aria-label',`${index+1} of ${this.plan.items.length}: ${item.label}`);
        const button = document.createElement('button'); button.type = 'button';
        button.className = item.type === 'image' ? 'ck-finish-gallery__image' : 'ck-finish-gallery__play';
        button.dataset[item.type === 'image' ? 'galleryZoom' : 'galleryPlay'] = index;
        button.setAttribute('aria-label',(item.type === 'image' ? 'Enlarge ' : 'Play ') + item.label.toLowerCase());
        const img = this.image(item,index === 0); button.append(img);
        if(item.type !== 'image') {const icon=document.createElement('span'); icon.textContent=item.type==='model'?'3D':'▶';icon.setAttribute('aria-hidden','true');button.append(icon);}
        else if(index===0) {const hint=document.createElement('span');hint.className='ck-finish-gallery__zoom-hint';hint.textContent='Tap to enlarge';hint.setAttribute('aria-hidden','true');button.append(hint);}
        slide.append(button);this.track.append(slide);
        const thumb=document.createElement('button');thumb.type='button';thumb.className='ck-finish-gallery__thumb';thumb.dataset.gallerySelect=index;
        thumb.setAttribute('aria-label',`${index+1}: ${item.label}`);thumb.setAttribute('aria-pressed',String(index===0));
        const preview=document.createElement('img');preview.src=item.thumb;preview.alt='';preview.loading='lazy';preview.width=76;preview.height=76;thumb.append(preview);
        if(item.type==='video'||item.type==='external_video'){const icon=document.createElement('span');icon.textContent='▶';icon.setAttribute('aria-hidden','true');thumb.append(icon);}
        this.thumbs.append(thumb);
      });
      this.querySelector('[data-gallery-selected]').textContent = this.plan.selected ? 'Selected: ' + finishLabel(this.plan.selected) : 'Choose your fabric and colour below';
      this.querySelectorAll('[data-gallery-step]').forEach(button => button.hidden = this.plan.items.length < 2);
      this.thumbs.hidden = this.plan.items.length < 2;
      this.index = 0;this.track.scrollLeft=0;this.markActive(0);
    }
    image(item,eager=false) {
      const img=document.createElement('img');img.src=item.src;
      img.srcset=[360,540,750,1000,1300,1600].map(width=>{const url=new URL(item.src,location.href);url.searchParams.set('width',width);return `${url} ${width}w`;}).join(', ');
      img.sizes='(min-width: 1000px) 55vw, 100vw';img.alt=item.type==='image'&&item.finish ? this.data.title+' — '+finishLabel(item.finish) : (item.alt||this.data.title);
      img.loading=eager?'eager':'lazy';img.fetchPriority=eager?'high':'auto';img.width=item.width||1600;img.height=item.height||1600;return img;
    }
    select(index,smooth=true) {
      index=Math.max(0,Math.min(this.plan.items.length-1,index));
      this.track.scrollTo({left:index*this.track.clientWidth,behavior:smooth&&!matchMedia('(prefers-reduced-motion: reduce)').matches?'smooth':'instant'});
      this.markActive(index);
    }
    markActive(index) {
      if(!this.plan)return;
      index=Math.max(0,Math.min(this.plan.items.length-1,index));this.index=index;
      const item=this.plan.items[index];
      this.thumbs.querySelectorAll('button').forEach((button,i)=>button.setAttribute('aria-pressed',String(i===index)));
      this.querySelector('[data-gallery-caption]').textContent=item.caption;
      this.querySelector('[data-gallery-count]').textContent=`${index+1} / ${this.plan.items.length}`;
      this.querySelector('[data-gallery-step="-1"]').disabled=index===0;
      this.querySelector('[data-gallery-step="1"]').disabled=index===this.plan.items.length-1;
      [...this.track.children].forEach((slide,i)=>{if(i!==index){slide.querySelectorAll('video').forEach(video=>video.pause());const frame=slide.querySelector('iframe');if(frame)this.restorePoster(slide,i);}});
    }
    restorePoster(slide,index) {
      const button=document.createElement('button');button.type='button';button.className='ck-finish-gallery__play';button.dataset.galleryPlay=index;button.setAttribute('aria-label','Play video');button.append(this.image(this.plan.items[index]));const icon=document.createElement('span');icon.textContent='▶';button.append(icon);slide.replaceChildren(button);
    }
    click(event) {
      const target=event.target.closest('button');if(!target||!this.contains(target)||!this.plan)return;
      if(target.hasAttribute('data-gallery-step'))this.select(this.index+Number(target.dataset.galleryStep));
      if(target.hasAttribute('data-gallery-select'))this.select(Number(target.dataset.gallerySelect));
      if(target.hasAttribute('data-gallery-close'))this.dialog.close();
      if(target.hasAttribute('data-gallery-zoom')){
        const item=this.plan.items[Number(target.dataset.galleryZoom)];const img=document.createElement('img');img.src=item.zoom;img.alt=this.data.title+' — '+(finishLabel(item.finish)||'reference photo');
        this.querySelector('[data-gallery-zoom-content]').replaceChildren(img);this.querySelector('[data-gallery-zoom-caption]').textContent=item.caption;this.dialog.showModal();
      }
      if(target.hasAttribute('data-gallery-play')){
        const item=this.plan.items[Number(target.dataset.galleryPlay)];const template=this.querySelector(`template[data-gallery-media="${item.id}"]`);if(!template)return;
        const slide=target.parentElement;slide.replaceChildren(template.content.cloneNode(true));
        const video=slide.querySelector('video');if(video)video.play().catch(()=>{});
      }
    }
  });
}

